import styled from '@emotion/styled';
import { Model, Pie1D, Pie1DData } from '@arra/core';
import { useTranslation } from 'react-i18next';
import {
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/src/component/DefaultTooltipContent';
import keyify from '../project/parameters/keyify';
import { getCategoryTranslationPrefix } from './Bar1DComponent';
import {
  SeedsTooltipComponent,
  SeedsTooltipRow,
} from './SeedsTooltipComponent';
import { co2Units, humanizeValue } from '../actions/page/ActionCard';

interface CustomTooltip extends TooltipProps<ValueType, NameType> {
  unit: string | undefined;
  translationPrefix: string;
  impactScope: string | undefined;
}

const CustomTooltipComponent = ({
  active,
  payload,
  unit,
  translationPrefix,
  impactScope,
}: CustomTooltip): JSX.Element => {
  const { t } = useTranslation(['project', 'model']);
  if (active && payload && payload.length > 0) {
    const category = payload[0].payload;
    const humanizedImpact =
      unit && impactScope
        ? humanizeValue(unit, Number(category.value), impactScope)
        : null;

    return (
      <SeedsTooltipComponent>
        <SeedsTooltipRow
          color={category.fill}
          category={t(`${translationPrefix}.data.${keyify(category.name)}`)}
          value={humanizedImpact?.value || category.value}
          unit={humanizedImpact?.unit || unit}
        />
      </SeedsTooltipComponent>
    );
  }
  return <></>;
};

const Pie1DComponent = ({
  result,
  model,
}: {
  result: Pie1D;
  model?: Model;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const translationPrefix = `model:${keyify(model?.name).toLowerCase()}.result.${result.code?.toLowerCase()}`;
  const impactScope =
    result.unit && co2Units.has(result.unit) ? 'co2' : result.code;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={result.data}
          cx={'50%'}
          cy={'50%'}
          innerRadius="0%"
          outerRadius="100%"
          dataKey="value"
        />
        <Tooltip
          content={
            <CustomTooltipComponent
              unit={result.unit}
              translationPrefix={translationPrefix}
              impactScope={impactScope}
            />
          }
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 1000 }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
`;

export const Pie1DLegend = ({
  result,
  model,
}: {
  result: Pie1D;
  model?: Model;
}) => {
  const { t } = useTranslation(['project', 'model']);
  const data = JSON.parse(
    JSON.stringify(result.data?.sort((a, b) => b.value - a.value)),
  );
  const total = data.reduce(
    (total: number, row: Pie1DData) => total + row.value,
    0,
  );
  const translationPrefix = getCategoryTranslationPrefix(model, result);
  const impactScope =
    result.unit && co2Units.has(result.unit) ? 'co2' : result.code;

  return (
    <LegendContainer>
      <p className="h6r underline">{t('project:results-page.legend')}</p>
      <div>
        {data.map((row: Pie1DData) => (
          <>
            {row.value > 0 && (
              (() => {
                const humanizedImpact =
                  result.unit && impactScope
                    ? humanizeValue(result.unit, Number(row.value), impactScope)
                    : null;

                return (
                  <SeedsTooltipRow
                    key={row.name}
                    link={row.link}
                    color={row.fill}
                    category={
                      row.name.startsWith('#')
                        ? row.name.slice(1)
                        : t(`${translationPrefix}.data.${keyify(row.name)}`)
                    }
                    value={humanizedImpact?.value || row.value.toString()}
                    unit={humanizedImpact?.unit || t(`${translationPrefix}.unit`)}
                    percent={row.value / total}
                  />
                );
              })()
            )}
          </>
        ))}
      </div>
    </LegendContainer>
  );
};

export default Pie1DComponent;
